/**
 * Treatment step wizard content
 *
 * The per-step detail content (dates, doses, medications, uploads) that the
 * treatment hub renders for each step, plus the step helpers and the egg
 * freezing step-progress migration. Backed by persisted cycle data.
 */

import { useState, useCallback, useMemo, useEffect } from "react";
import { getTodayString } from "@src/packages/utils/date";
import {
  View,
  Pressable,
  ScrollView,
  TextInput,
  Text as RNText,
  StyleSheet,
} from "react-native";
import type { TextProps as RNTextProps } from "react-native";
import {
  CompactActionButton,
  DateField,
  MoonTimeField,
  MoonUnitToggle,
  MoonValueInput,
  InlineOptionPicker,
  MoonSegmentedControl,
  SectionTitle,
  cardSurface,
  TextField,
} from "@louise/moon-ui";
import {
  Check,
  Plus,
  Pill,
  Zap,
  RotateCcw,
  ChevronDown,
} from "lucide-react-native";
import type {
  TreatmentStepProgress,
  LocalMedication,
} from "@louise/care/treatment-phases";
import { useMedications } from "@louise/care/hooks/useMedications";
import { numericStepValue } from "@src/packages/patients/hooks/numericStepValue";
import {
  resolveStepFieldUnits,
  tf,
  type TreatmentStepConfig,
} from "./treatmentFlows";
import { useLocale, useTranslations } from "@src/packages/i18n";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { formatDate } from "@src/packages/i18n/formatters";
import { getStepFieldToggleIcons } from "./stepFieldToggles";
import { TreatmentUploadField } from "./TreatmentUploadField";
import { semanticColors, withAlpha } from "@louise/theme/tokens";

// The flows and the graph that resolves them live in `./treatmentFlows` so they
// can be read without a React Native runtime. Re-exported here because this
// module has always been where the rest of the app imports them from, and
// moving them is not a reason to touch every call site.
export { getStepsForCycle, getStepsForPerspective } from "./treatmentFlows";
export type {
  ConditionalCycleOptions,
  StepPerspective,
  TreatmentStepConfig,
} from "./treatmentFlows";

type DMSansTextProps = RNTextProps & { className?: string };

function resolveDMSansFamily(fontWeight: unknown) {
  const weight =
    typeof fontWeight === "number"
      ? fontWeight
      : typeof fontWeight === "string"
        ? Number.parseInt(fontWeight, 10)
        : 400;

  if (Number.isFinite(weight) && weight >= 700) return "DMSans_700Bold";
  if (Number.isFinite(weight) && weight >= 500) return "DMSans_500Medium";
  return "DMSans_400Regular";
}

function Text({ className, style, ...rest }: DMSansTextProps) {
  const flattenedStyle = StyleSheet.flatten(style);
  const fontFamily = resolveDMSansFamily(flattenedStyle?.fontWeight);

  return (
    <RNText
      {...rest}
      className={["font-dm-sans", className].filter(Boolean).join(" ")}
      style={[{ fontFamily }, style, { fontFamily }]}
    />
  );
}

// =============================================================================
// Types & Configuration
// =============================================================================

/** Step definition for a treatment flow */

// Color constants
const violetAccent = "#C4B5FD";
const greenAccent = "#4ADE80";

// =============================================================================
// DateTime Field Picker Component
// =============================================================================

interface DateTimeFieldPickerProps {
  value: string | null;
  onChange: (isoString: string | null) => void;
  placeholder?: string;
}

export function DateTimeFieldPicker({
  value,
  onChange,
  placeholder,
}: DateTimeFieldPickerProps) {
  const dateString = useMemo(() => {
    if (!value || typeof value !== "string") return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }, [value]);

  const timeString = useMemo(() => {
    if (!value || typeof value !== "string") return "";
    const date = new Date(value);
    if (isNaN(date.getTime())) return "";
    const hours = date.getHours().toString().padStart(2, "0");
    const minutes = date.getMinutes().toString().padStart(2, "0");
    return `${hours}:${minutes}`;
  }, [value]);

  const handleDateChange = useCallback(
    (nextDate: string) => {
      if (!nextDate) {
        onChange(null);
        return;
      }
      const [hours, minutes] = (timeString || "12:00").split(":").map(Number);
      const combined = new Date(`${nextDate}T00:00:00`);
      combined.setHours(hours ?? 12, minutes ?? 0, 0, 0);
      onChange(combined.toISOString());
    },
    [timeString, onChange],
  );

  const handleTimeChange = useCallback(
    (nextTime: string) => {
      const baseDateString =
        dateString ||
        (() => {
          const today = new Date();
          const y = today.getFullYear();
          const m = String(today.getMonth() + 1).padStart(2, "0");
          const d = String(today.getDate()).padStart(2, "0");
          return `${y}-${m}-${d}`;
        })();
      const [hours, minutes] = (nextTime || "12:00").split(":").map(Number);
      const combined = new Date(`${baseDateString}T00:00:00`);
      combined.setHours(hours ?? 12, minutes ?? 0, 0, 0);
      onChange(combined.toISOString());
    },
    [dateString, onChange],
  );

  return (
    <View className="flex-row gap-2">
      <View style={{ flex: 1 }}>
        <DateField
          label="Date"
          value={dateString}
          onChange={handleDateChange}
        />
      </View>
      <View style={{ flex: 1 }}>
        <MoonTimeField
          label="Time"
          placeholder="HH:MM"
          value={timeString}
          onChange={handleTimeChange}
        />
      </View>
    </View>
  );
}

// =============================================================================
// Step Wizard Content Component
// =============================================================================

const TRIGGER_DEFAULT_DOSES: Record<string, string> = {
  Ovidrel: "250 mcg",
  Pregnyl: "10,000 IU",
  Novarel: "10,000 IU",
};

const TRIGGER_DOSE_UNITS = ["mcg", "IU", "mg", "units"] as const;

// Splits a stored "250 mcg" / "10,000 IU" combined dose string into the
// amount and unit halves so the picker can edit each independently.
function parseDoseString(value: string): { amount: string; unit: string } {
  const trimmed = value.trim();
  if (!trimmed) return { amount: "", unit: "" };
  const match = /^([\d.,]+)\s*(.*)$/.exec(trimmed);
  if (!match) return { amount: "", unit: trimmed };
  return { amount: match[1] ?? "", unit: (match[2] ?? "").trim() };
}

function joinDose(amount: string, unit: string): string {
  const a = amount.trim();
  const u = unit.trim();
  if (a && u) return `${a} ${u}`;
  return a || u;
}

function DoseField({
  value,
  onChange,
  placeholder,
  dropdownKey,
  dropdownOpen,
  setDropdownOpen,
  colors,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  dropdownKey: string;
  dropdownOpen: string | null;
  setDropdownOpen: (next: string | null) => void;
  colors: {
    fieldBg: string;
    fieldBorder: string;
    fieldText: string;
    fieldPlaceholder: string;
    selectedBorder: string;
    label: string;
    dropdownBg: string;
    dropdownBorder: string;
    dropdownItem: string;
    dropdownSep: string;
    fieldSelected: string;
  };
}) {
  const { amount, unit } = parseDoseString(value);
  const isOpen = dropdownOpen === dropdownKey;
  const capturePress = usePressCapture("DoseField");
  return (
    <View style={{ position: "relative" }}>
      <View className="flex-row">
        <TextInput
          value={amount}
          onChangeText={(text) => onChange(joinDose(text, unit))}
          placeholder={placeholder}
          placeholderTextColor={colors.fieldPlaceholder}
          keyboardType="decimal-pad"
          style={{
            flex: 1,
            backgroundColor: colors.fieldBg,
            borderWidth: 1,
            borderColor: colors.fieldBorder,
            borderTopLeftRadius: 8,
            borderBottomLeftRadius: 8,
            borderRightWidth: 0,
            paddingHorizontal: 12,
            paddingVertical: 8,
            fontSize: 13,
            color: colors.fieldText,
          }}
        />
        <Pressable
          onPress={() => {
            capturePress(pressLabel(unit || "unit"), "dose_unit_dropdown");
            setDropdownOpen(isOpen ? null : dropdownKey);
          }}
          className="flex-row items-center justify-center"
          style={{
            gap: 4,
            paddingHorizontal: 12,
            borderWidth: 1,
            borderColor: isOpen ? colors.selectedBorder : colors.fieldBorder,
            borderTopRightRadius: 8,
            borderBottomRightRadius: 8,
            backgroundColor: colors.fieldBg,
            minWidth: 70,
          }}
        >
          <Text
            style={{
              fontSize: 13,
              color: unit ? colors.fieldText : colors.fieldPlaceholder,
            }}
          >
            {unit || "unit"}
          </Text>
          <ChevronDown size={14} color={colors.label} />
        </Pressable>
      </View>
      {isOpen && (
        <View
          style={{
            marginTop: 4,
            backgroundColor: colors.dropdownBg,
            borderWidth: 1,
            borderColor: colors.dropdownBorder,
            borderRadius: 8,
            overflow: "hidden",
          }}
        >
          {TRIGGER_DOSE_UNITS.map((opt) => {
            const isSelected = unit === opt;
            return (
              <Pressable
                key={opt}
                onPress={() => {
                  capturePress(pressLabel(opt), "dose_unit_option");
                  onChange(joinDose(amount, opt));
                  setDropdownOpen(null);
                }}
                className="px-3 py-2.5"
                style={{
                  backgroundColor: isSelected
                    ? withAlpha(colors.fieldSelected, 0.09)
                    : "transparent",
                  borderBottomWidth: 1,
                  borderBottomColor: colors.dropdownSep,
                }}
              >
                <Text
                  style={{
                    fontSize: 13,
                    color: isSelected
                      ? colors.fieldSelected
                      : colors.dropdownItem,
                    fontWeight: isSelected ? "600" : "400",
                  }}
                >
                  {opt}
                </Text>
              </Pressable>
            );
          })}
        </View>
      )}
    </View>
  );
}

export interface StepWizardContentProps {
  step: TreatmentStepConfig;
  progress: TreatmentStepProgress | null;
  onComplete: (data?: Record<string, string | number | boolean | null>) => void;
  onSkip: () => void;
  onUndo: () => void;
  onUpdateData: (
    data: Record<string, string | number | boolean | null>,
  ) => void;
  onAddMedication: () => void;
  onAddTriggerShot?: () => void;
  linkedMedications: LocalMedication[];
  /** Theme variant — "dark" (olly default) or "light" (moon EMBER) */
  theme?: "dark" | "light";
  /** Override the default dark DateTimeFieldPicker with a custom component */
  renderDateTimePicker?: (props: {
    value: string | null;
    onChange: (isoString: string | null) => void;
    placeholder?: string;
  }) => React.ReactNode;
  /** Renders each logged medication. */
  renderMedicationCard: (med: LocalMedication) => React.ReactNode;
  /** Override the empty medication state when this wizard is embedded in a section card. */
  renderMedicationEmptyState?: (options: {
    onAddMedication: () => void;
  }) => React.ReactNode;
  /**
   * Override the schedulable-procedure planning UI. When provided and the step
   * is a procedure awaiting scheduling (no date / future date), the bespoke
   * inline date/time form + Schedule CTA are suppressed and this canonical row
   * is rendered instead — it opens the app's normal appointment flow.
   */
  renderScheduleAction?: () => React.ReactNode;
  /**
   * Override the outcome form for a completed/past egg-retrieval procedure.
   * When provided, the scheduling row remains the entry point for a future
   * procedure, while this row is the sole action once retrieval has happened.
   */
  renderOutcomeAction?: () => React.ReactNode;
  /**
   * Override the inline form for a lab-report step (`resultsUpload`). When
   * provided, the bespoke fields + Confirm CTA are suppressed and this
   * canonical row is rendered instead — it opens the app's normal
   * document/test-result upload flow.
   */
  renderResultsAction?: () => React.ReactNode;
  /**
   * Override the inline form for a `detailsModal` step (fertilization, embryo
   * development, freezing, screening, PGT). When provided, the bespoke fields +
   * Confirm CTA are suppressed and this canonical row is rendered instead — it
   * opens StepDetailsModal, which holds the same inputs.
   */
  renderDetailsAction?: () => React.ReactNode;
  /** Render the real specimen selector for IUI collection/procedure steps. */
  renderLinkageAction?: () => React.ReactNode;
  /**
   * Render a frozen-specimen picker for a step field of type `specimen`.
   * Wired at the moon call site so this package stays free of app-level
   * imports (picker lives under apps/moon). When omitted, a specimen field
   * still shows its label but the input is empty — safe for tests and
   * screens that never expose a specimen-typed field.
   */
  renderSpecimenField?: (options: {
    fieldName: string;
    value: string | null;
    onChange: (specimenId: string | null) => void;
  }) => React.ReactNode;
  /** Medication add modals persist their own data, so embedded medication steps do not need a second save CTA. */
  hidePendingCompleteAction?: boolean;
  /** Suppress the prompt title/subtitle when the surrounding section already provides context. */
  hidePrompt?: boolean;
  /** Override the primary pending-state action label for embedded contexts. */
  pendingActionLabel?: string;
  /** Hide the skip ("I didn't have…") action — e.g. once the user has logged
   * data that contradicts skipping this step. */
  hideSkip?: boolean;
}

/**
 * Steps whose inputs used to render as a bespoke inline form in the step card
 * now show a canonical row that opens a canonical dialogue (StepDetailsModal)
 * holding the same inputs. Keyed by step id so it applies across every flow.
 * Interim until each step gets its proper redesign (#2094–#2100 / #2041).
 */
const DETAILS_MODAL_STEP_IDS = new Set([
  "screening",
  "fertilization",
  "embryo_development",
  "freezing",
  "pgt_testing",
  // Semen analysis. The report upload is not a substitute for the figures:
  // nothing reads a PDF, so a patient holding her results on paper — or reading
  // them off a portal — had no way to record a single number. It keeps its
  // upload row as well; see the row pair below.
  "analysis",
]);

export function stepUsesDetailsModal(stepId: string): boolean {
  return DETAILS_MODAL_STEP_IDS.has(stepId);
}

function getHubPromptTitle(
  step: TreatmentStepConfig,
  opts?: { isPlanning?: boolean },
) {
  if (step.contentType === "medications") return "Medication details";
  if (opts?.isPlanning && step.schedulingPrompt) return step.schedulingPrompt;
  return step.prompt ?? step.title;
}

function getHubPromptSubtitle(step: TreatmentStepConfig) {
  if (step.id === "pre_treatment") {
    return "Add priming medications used before stimulation.";
  }
  if (step.id === "protocol") {
    return "Add stimulation medications, dose changes, trigger shots, and clinic instructions.";
  }
  if (step.id === "luteal_phase") {
    return "Add support medications used after transfer.";
  }
  if (step.contentType === "medications") return step.description;
  return step.description;
}

export function StepWizardContent({
  step,
  progress,
  onComplete,
  onSkip,
  onUndo,
  onUpdateData,
  onAddMedication,
  onAddTriggerShot,
  linkedMedications,
  theme = "dark",
  renderDateTimePicker,
  renderMedicationCard,
  renderMedicationEmptyState,
  renderScheduleAction,
  renderOutcomeAction,
  renderResultsAction,
  renderDetailsAction,
  renderLinkageAction,
  renderSpecimenField,
  hidePendingCompleteAction = false,
  hidePrompt = false,
  pendingActionLabel,
  hideSkip = false,
}: StepWizardContentProps) {
  const t = useTranslations();
  const capturePress = usePressCapture("StepWizardContent");
  const isLight = theme === "light";
  const resolvedPendingActionLabel =
    pendingActionLabel ?? t("care.treatment_flows.actions.confirm_save");
  const { locale } = useLocale();
  const c = isLight
    ? {
        prompt: semanticColors.text.primary,
        promptBg: withAlpha(semanticColors.accent.default, 0.05),
        promptBorder: withAlpha(semanticColors.accent.default, 0.1),
        label: semanticColors.text.secondary,
        medName: semanticColors.text.primary,
        medBg: withAlpha(semanticColors.accent.default, 0.04),
        medBorder: withAlpha(semanticColors.accent.default, 0.12),
        medDose: semanticColors.text.secondary,
        medDate: semanticColors.text.tertiary,
        fieldBg: semanticColors.surface.muted,
        fieldBorder: semanticColors.border.subtle,
        fieldText: semanticColors.text.primary,
        fieldPlaceholder: semanticColors.text.faint,
        fieldSelected: semanticColors.accent.default,
        selectedBg: withAlpha(semanticColors.accent.default, 0.1),
        selectedBorder: withAlpha(semanticColors.accent.default, 0.3),
        skipText: semanticColors.text.tertiary,
        done: semanticColors.status.success,
        undo: semanticColors.text.tertiary,
        undoBg: semanticColors.surface.muted,
        undoBgPressed: semanticColors.overlay.pressed,
        accent: semanticColors.accent.default,
        accentBg: semanticColors.accent.tint,
        accentBorder: withAlpha(semanticColors.accent.default, 0.15),
        noMedBorder: semanticColors.border.strong,
        noMedText: semanticColors.text.tertiary,
        completedMedText: semanticColors.text.secondary,
        descText: semanticColors.text.secondary,
        dropdownBg: semanticColors.surface.raised,
        dropdownBorder: semanticColors.border.strong,
        dropdownItem: semanticColors.text.primary,
        dropdownSep: semanticColors.border.subtle,
        customBg: semanticColors.surface.raised,
        customBorder: withAlpha(semanticColors.accent.default, 0.15),
        customAccent: semanticColors.accent.default,
        customInputBg: semanticColors.surface.muted,
        customInputBorder: semanticColors.border.strong,
        customInputText: semanticColors.text.primary,
        otherBg: semanticColors.surface.muted,
        otherBorder: "#C8BDB5",
        otherIcon: semanticColors.text.tertiary,
        otherText: semanticColors.text.secondary,
        secondMedLabel: semanticColors.text.secondary,
        menuBg: semanticColors.background.app,
        menuBorder: semanticColors.border.strong,
        menuText: semanticColors.text.secondary,
        menuCancel: semanticColors.text.tertiary,
      }
    : {
        prompt: "#e2e8f0",
        promptBg: "rgba(196,181,253,0.05)",
        promptBorder: "rgba(196,181,253,0.1)",
        label: "#94a3b8",
        medName: "#e2e8f0",
        medBg: "rgba(2,6,23,0.4)",
        medBorder: "rgba(167,139,250,0.15)",
        medDose: "#94a3b8",
        medDate: "#64748b",
        fieldBg: "rgba(2,6,23,0.5)",
        fieldBorder: "rgba(255,255,255,0.1)",
        fieldText: "#e2e8f0",
        fieldPlaceholder: "#475569",
        fieldSelected: "#fbbf24",
        selectedBg: "rgba(251,191,36,0.2)",
        selectedBorder: "rgba(251,191,36,0.5)",
        skipText: "#64748b",
        done: greenAccent,
        undo: "#94a3b8",
        undoBg: "rgba(255,255,255,0.03)",
        undoBgPressed: "rgba(255,255,255,0.08)",
        accent: violetAccent,
        accentBg: "rgba(167,139,250,0.1)",
        accentBorder: "rgba(167,139,250,0.2)",
        noMedBorder: "rgba(255,255,255,0.1)",
        noMedText: "#64748b",
        completedMedText: "#cbd5e1",
        descText: "#94a3b8",
        dropdownBg: "rgba(15,23,42,0.95)",
        dropdownBorder: "rgba(255,255,255,0.1)",
        dropdownItem: "#e2e8f0",
        dropdownSep: "rgba(255,255,255,0.05)",
        customBg: "rgba(15,23,42,0.8)",
        customBorder: "rgba(196,181,253,0.2)",
        customAccent: "#C4B5FD",
        customInputBg: "rgba(15,23,42,0.5)",
        customInputBorder: "rgba(255,255,255,0.1)",
        customInputText: "#ffffff",
        otherBg: "rgba(30,41,59,0.5)",
        otherBorder: "#334155",
        otherIcon: "#64748B",
        otherText: "#94a3b8",
        secondMedLabel: "#94a3b8",
        menuBg: "#0f172a",
        menuBorder: "rgba(255,255,255,0.1)",
        menuText: "#94a3b8",
        menuCancel: "#64748b",
      };

  const status = progress?.status ?? "pending";
  const savedData = useMemo(() => progress?.data ?? {}, [progress?.data]);
  // Buffer field changes locally — only flushed on "Confirm & Save"
  const [localData, setLocalData] =
    useState<Record<string, string | number | boolean | null>>(savedData);
  // Sync local state when saved data changes (e.g., after a refetch)
  useEffect(() => {
    setLocalData(savedData);
  }, [savedData]);
  const stepData = localData;

  // Are we PLANNING (the procedure hasn't happened yet) or LOGGING
  // (the procedure already happened)? Drives:
  //   - which prompt to show: schedulingPrompt vs prompt
  //   - which validations gate the button: date alone vs all required
  //   - which label the button takes: Schedule vs Confirm & Save
  //
  // Planning when either: no procedure/transfer date has been entered yet,
  // OR the date entered is in the future. Logging when a today/past date
  // is in the buffer (the user is describing what just happened).
  const procedureDate = useMemo<{
    state: "missing" | "future" | "today_or_past";
    dateIso: string | null;
  }>(() => {
    // Local, not UTC, and through the helper this file already imports.
    // `CycleDetailScreen` writes the phase and resolves the calendar sync from
    // the device's own calendar day, so a UTC "today" put the two a day apart for
    // a few hours daily: the card could offer to schedule a date the sync had
    // already decided was past, or call a future one dated.
    const todayIso = getTodayString();
    let seenDateIso: string | null = null;
    // `collectionDate` belongs here as much as the other two: it is the date
    // field `PROCEDURE_SCHEDULING` declares for `sperm_collection`. Left out, a
    // recorded collection date was invisible to every decision below — the step
    // stayed permanently in "planning", so the card kept offering "Schedule the
    // sperm collection" over a collection the patient had already dated, the
    // prompt stayed the scheduling one, and only the date field was allowed to
    // render.
    for (const fieldName of [
      "procedureDate",
      "transferDate",
      "collectionDate",
    ] as const) {
      const raw = localData[fieldName];
      if (typeof raw !== "string" || raw.length < 10) continue;
      const dateOnly = raw.slice(0, 10);
      seenDateIso = dateOnly;
      if (dateOnly > todayIso) return { state: "future", dateIso: dateOnly };
    }
    return seenDateIso
      ? { state: "today_or_past", dateIso: seenDateIso }
      : { state: "missing", dateIso: null };
  }, [localData]);
  const procedureDateState = procedureDate.state;
  // Pending-without-date and future-date both mean "we're planning". Only
  // a today/past date in the buffer means "logging the past event".
  // `schedulable` gates this — lab-outcome steps don't have a planning mode.
  // Sperm collection turns off scheduling once the patient says her sample
  // is already frozen: there is nothing to schedule for a vial that was
  // collected months ago. Without this short-circuit the schedule chrome
  // would still gate the fields and she could never advance past "when
  // is it planned?" — but the answer is "never, it happened already".
  const isFrozenSpermCollection =
    step.id === "sperm_collection" && localData.spermType === "frozen";
  const isPlanningProcedure =
    step.schedulable === true &&
    procedureDateState !== "today_or_past" &&
    !isFrozenSpermCollection;

  // When a caller supplies a canonical schedule row (moon hub), a procedure
  // awaiting scheduling delegates to the normal appointment flow instead of
  // rendering the inline date/time form + Schedule CTA.
  // Already scheduled through the canonical appointment flow, waiting for
  // the day: the schedule CTA would be a lie ("nothing planned yet"), so the
  // row gives way to a dated confirmation until the date arrives (#3273).
  const showScheduledConfirmation =
    step.schedulable === true &&
    procedureDateState === "future" &&
    renderScheduleAction != null;
  const showScheduleRow =
    isPlanningProcedure &&
    renderScheduleAction != null &&
    !showScheduledConfirmation;
  /**
   * The same line, for a procedure whose date has passed. See the render site for
   * why it states the date rather than the event.
   *
   * `showScheduledConfirmation` stops at `"future"`, so a step the patient had
   * dated in the past showed nothing dated at all: the schedule row is correctly
   * gone, and what took its place was the field list. Scoped to the hub the same
   * way its future twin is — `renderScheduleAction != null` is what says this
   * caller has a canonical scheduling surface at all.
   */
  const showCollectedConfirmation =
    step.schedulable === true &&
    procedureDateState === "today_or_past" &&
    renderScheduleAction != null;

  /**
   * What the procedure yielded, when a count is on record.
   *
   * Read from the step's own buffered data — `deriveStepProgress` maps the
   * phase's `retrieved_oocytes` metric onto `eggsCollected`, so this is the
   * number she entered, wherever she entered it from.
   */
  const recordedProcedureCount = useMemo(
    () => numericStepValue(localData.eggsCollected),
    [localData],
  );

  // Retrieval scheduling and retrieval outcomes are different user actions.
  // Keep the appointment row for a missing/future date, and delegate the
  // happened-state to the caller when it has a canonical outcome surface.
  const showOutcomeRow =
    (step.id === "egg_retrieval" || step.id === "outcome") &&
    !isPlanningProcedure &&
    renderOutcomeAction != null;

  // A lab-report step (semen analysis) delegates to the normal document/
  // test-result upload flow via a canonical row, instead of an inline form.
  const showResultsRow =
    step.resultsUpload === true && renderResultsAction != null;

  // Interim: a bespoke-form step (fertilization, embryo development, freezing,
  // screening, PGT) shows a canonical row that opens StepDetailsModal instead
  // of rendering its inputs inline.
  const showDetailsRow =
    stepUsesDetailsModal(step.id) && renderDetailsAction != null;

  // Required-field gating, context-aware:
  // - Scheduling (future date): only date/datetime fields must be filled.
  //   Counts like eggsCollected don't exist yet — the procedure hasn't
  //   happened. Forcing them would block scheduling entirely.
  // - Completing (today or past date): all required fields must be filled.
  //   Numeric zero is a valid clinical result and must not be treated as
  //   missing (for example, a retrieval with zero oocytes).
  const missingRequiredField = useMemo(() => {
    for (const field of step.fields ?? []) {
      if (!field.required) continue;
      const isDateField = field.type === "datetime" || field.type === "date";
      // In planning mode (no date or future date) we only block on the
      // date itself. Counts like eggsCollected can't exist yet.
      if (isPlanningProcedure && !isDateField) continue;
      const value = localData[field.name];
      if (value === undefined || value === null || value === "") return field;
      if (field.type === "number") {
        const num = typeof value === "number" ? value : Number(value);
        if (!Number.isFinite(num) || num < 0) return field;
      }
    }
    return null;
  }, [step.fields, localData, isPlanningProcedure]);
  const canComplete = !missingRequiredField;
  const effectivePendingLabel = isPlanningProcedure
    ? t("care.treatment_flows.actions.schedule")
    : resolvedPendingActionLabel;
  const allMedications = linkedMedications;
  const [dropdownOpen, setDropdownOpen] = useState<string | null>(null);
  const [customMedConfirmed, setCustomMedConfirmed] = useState(false);

  // Fetch medication catalog for dropdown fields
  const hasDropdownField = step.fields?.some((f) => f.type === "dropdown");
  const { data: dbMedications = [] } = useMedications({
    enabled: !!hasDropdownField,
  });

  const handleFieldChange = useCallback(
    (fieldName: string, value: string | number | null) => {
      setLocalData((prev) => ({ ...prev, [fieldName]: value }));
    },
    [],
  );

  // One renderer for a select field, wherever the step shows it: the mode
  // questions above the schedule cascade and the field list below it.
  const renderSelectField = (
    field: NonNullable<TreatmentStepConfig["fields"]>[number],
    options: string[],
  ) => {
    // Untranslated options fall back to the raw value,
    // capitalised as the old chip's text transform did.
    const optionLabel = (opt: string) =>
      tf(
        t,
        `care.treatment_flows.options.${opt}`,
        opt.charAt(0).toUpperCase() + opt.slice(1),
      );
    const toggleIcons = getStepFieldToggleIcons(field);
    const value = stepData[field.name];
    // An either/or such as partner or donor is a two-way
    // toggle, each side with its icon.
    return toggleIcons ? (
      <MoonSegmentedControl
        tabs={options.map((opt) => ({
          value: opt,
          label: optionLabel(opt),
          icon: toggleIcons[opt],
          action: "step_field_option",
        }))}
        active={typeof value === "string" && value ? value : null}
        onChange={(opt) => handleFieldChange(field.name, opt)}
        optionRole="radio"
        accessibilityLabel={field.label}
        tone={isLight ? "default" : "inverse"}
        style={{ marginTop: 0 }}
      />
    ) : (
      <InlineOptionPicker
        options={options.map((opt) => ({
          id: opt,
          label: optionLabel(opt),
          action: "step_field_option",
        }))}
        value={(stepData[field.name] as string) ?? ""}
        onChange={(opt) => handleFieldChange(field.name, opt)}
        wrap
      />
    );
  };

  return (
    <View style={{ gap: 20 }}>
      {/* Completed / Skipped state */}
      {(status === "completed" || status === "skipped") && (
        <View style={{ alignItems: "center", gap: 12 }}>
          <View className="flex-row items-center gap-2">
            <Check
              size={16}
              color={status === "completed" ? c.done : c.skipText}
              strokeWidth={3}
            />
            <Text
              style={{
                fontSize: 14,
                fontWeight: "500",
                color: status === "completed" ? c.done : c.skipText,
              }}
            >
              {status === "completed"
                ? t("care.treatment_flows.state.step_complete")
                : t("care.treatment_flows.state.step_skipped")}
            </Text>
          </View>

          {/* Show saved data summary for completed steps */}
          {status === "completed" && allMedications.length > 0 && (
            <View style={{ width: "100%", gap: 0 }}>
              {allMedications.slice(0, 4).map((med, i) => (
                <View
                  key={med.id}
                  style={{
                    borderTopWidth: i > 0 ? 1 : 0,
                    borderTopColor: semanticColors.border.subtle,
                  }}
                >
                  {renderMedicationCard(med)}
                </View>
              ))}
              {allMedications.length > 4 && (
                <Text style={{ fontSize: 11, color: c.medDate, marginTop: 2 }}>
                  {t("care.treatment_flows.medications.more_count", {
                    params: { count: String(allMedications.length - 4) },
                  })}
                </Text>
              )}
            </View>
          )}

          {/* Undo button */}
          <Pressable
            onPress={() => {
              capturePress(
                pressLabel(t("care.treatment_flows.actions.undo")),
                "care.treatment_flows.actions.undo",
              );
              onUndo();
            }}
            className="flex-row items-center gap-1.5 rounded-lg px-3 py-1.5"
            style={({ pressed }) => ({
              backgroundColor: pressed ? c.undoBgPressed : c.undoBg,
              opacity: pressed ? 0.7 : 1,
            })}
          >
            <RotateCcw size={12} color={c.undo} />
            <Text style={{ fontSize: 12, color: c.undo }}>
              {t("care.treatment_flows.actions.undo")}
            </Text>
          </Pressable>
        </View>
      )}

      {/* Pending state - show form content */}
      {status === "pending" && (
        <>
          {/* Prompt */}
          {hidePrompt ||
          showScheduleRow ||
          showScheduledConfirmation ||
          showOutcomeRow ||
          showResultsRow ||
          showDetailsRow ? null : (
            <View style={{ gap: 5 }}>
              <SectionTitle level="subsection">
                {getHubPromptTitle(step, { isPlanning: isPlanningProcedure })}
              </SectionTitle>
              <Text
                style={{
                  fontSize: 12.5,
                  lineHeight: 18,
                  color: c.descText,
                }}
              >
                {getHubPromptSubtitle(step)}
              </Text>
            </View>
          )}

          {/* Medications Content */}
          {step.contentType === "medications" && (
            <View>
              {allMedications.length > 0 ? (
                <View
                  style={{
                    gap: 0,
                    marginBottom: 16,
                  }}
                >
                  {allMedications.slice(0, 4).map((med, i) => (
                    <View
                      key={med.id}
                      style={{
                        borderTopWidth: i > 0 ? 1 : 0,
                        borderTopColor: semanticColors.border.subtle,
                      }}
                    >
                      {renderMedicationCard(med)}
                    </View>
                  ))}
                  {allMedications.length > 4 && (
                    <Text
                      style={{
                        fontSize: 11,
                        color: c.medDate,
                        marginTop: 4,
                      }}
                    >
                      {t("care.treatment_flows.medications.more_count", {
                        params: {
                          count: String(allMedications.length - 4),
                        },
                      })}
                    </Text>
                  )}
                </View>
              ) : renderMedicationEmptyState ? (
                <View
                  style={{ marginBottom: hidePendingCompleteAction ? 0 : 16 }}
                >
                  {renderMedicationEmptyState({ onAddMedication })}
                </View>
              ) : (
                <View
                  style={[
                    isLight
                      ? cardSurface({ variant: "prompt" })
                      : {
                          borderRadius: 10,
                          borderWidth: 1,
                          borderStyle: "dashed",
                          borderColor: c.noMedBorder,
                        },
                    { padding: 16, alignItems: "center", marginBottom: 16 },
                  ]}
                >
                  <Pill
                    size={20}
                    color={c.noMedText}
                    style={{ marginBottom: 8 }}
                  />
                  <Text
                    style={{
                      fontSize: 12,
                      color: c.noMedText,
                      textAlign: "center",
                    }}
                  >
                    {t("care.treatment_flows.medications.none_logged")}
                  </Text>
                </View>
              )}

              {/* Add medication buttons */}
              <View style={{ gap: 10 }}>
                {allMedications.length > 0 ? (
                  <CompactActionButton
                    label={t(
                      "care.treatment_flows.actions.add_another_medication",
                    )}
                    onPress={onAddMedication}
                    leadingIcon={Plus}
                    variant="secondary"
                    minWidth={118}
                  />
                ) : null}

                {step.hasTriggerShot &&
                  onAddTriggerShot &&
                  (() => {
                    const hasTriggerMed = allMedications.some(
                      (m) => m.medicationRole === "trigger_shot",
                    );
                    return (
                      <CompactActionButton
                        label={
                          hasTriggerMed
                            ? t("care.treatment_flows.actions.trigger_added")
                            : t("care.treatment_flows.actions.add_trigger")
                        }
                        onPress={onAddTriggerShot}
                        leadingIcon={Zap}
                        variant="secondary"
                        minWidth={118}
                        disabled={hasTriggerMed}
                      />
                    );
                  })()}
              </View>
            </View>
          )}

          {/* Custom Fields — or, for an unscheduled procedure, the canonical
              row that hands off to the normal appointment flow. */}
          {/* A dated procedure whose date has passed said so nowhere. The future
              case has had its confirmation line since #3273; a past one fell
              through to the fields, so the one thing the patient had just
              recorded was legible only inside an input. Rendered beside the chain
              below rather than inside it, so it displaces nothing — the retrieval
              outcome row and the fields both still get their turn.

              "Dated", not "happened on". `deriveStepProgress` back-fills the
              step's date field from `phase.startDate` when the record carries
              none, so saving a collection with no date at all leaves today's date
              in that field — and a line reading "Happened on 26 Aug" would be
              asserting an event on a date she never gave. Naming the date without
              claiming the event is true either way. */}
          {showCollectedConfirmation ? (
            <View style={{ marginTop: 4 }}>
              <SectionTitle level="subsection" style={{ lineHeight: 18 }}>
                {t("care.treatment_flows.dated_on", {
                  params: {
                    date: formatDate(procedureDate.dateIso ?? "", locale),
                  },
                })}
              </SectionTitle>
              {/* What the procedure actually yielded, under the date it happened
                  on. The count is recorded through Cryo Vault and displayed on
                  the outcome tab, which left this tab naming a date and saying
                  nothing about the day itself — so the step she had filled read
                  as empty. Same words the recap uses, so one number is not
                  phrased two ways. */}
              {recordedProcedureCount != null ? (
                <Text
                  className="font-dm-sans"
                  style={{
                    marginTop: 2,
                    fontSize: 13,
                    lineHeight: 18,
                    color: c.descText,
                  }}
                >
                  {t(
                    recordedProcedureCount === 1
                      ? "care.treatment_hub.step_filled.eggs_collected_one"
                      : "care.treatment_hub.step_filled.eggs_collected_other",
                    { params: { count: String(recordedProcedureCount) } },
                  )}
                </Text>
              ) : null}
            </View>
          ) : null}
          {/* Mode questions rendered ABOVE the scheduling/outcome cascade so
              they're reachable in planning mode. Without this, a field like
              "Fresh or Frozen?" can't be answered until a date has been
              scheduled — but the answer is what tells us whether scheduling
              is needed at all. Only fields marked `showInPlanning: true`
              appear here; everything else stays in the normal field-list at
              the bottom of the cascade. */}
          {(() => {
            const modeFields = (step.fields ?? []).filter(
              (field) =>
                field.showInPlanning === true &&
                (!field.showIf ||
                  stepData[field.showIf.field] === field.showIf.equals),
            );
            if (modeFields.length === 0) return null;
            return (
              <View style={{ marginTop: 4, gap: 12 }}>
                {modeFields.map((field) => (
                  <View key={`mode-${field.name}`}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        fontWeight: "500",
                        color: c.label,
                        marginBottom: 6,
                      }}
                    >
                      {field.label}
                    </Text>
                    {field.type === "select" && field.options
                      ? renderSelectField(field, field.options)
                      : field.type === "specimen" && renderSpecimenField
                        ? renderSpecimenField({
                            fieldName: field.name,
                            value: (stepData[field.name] as string) ?? null,
                            onChange: (id) => handleFieldChange(field.name, id),
                          })
                        : null}
                  </View>
                ))}
              </View>
            );
          })()}
          {showScheduleRow ? (
            <View style={{ marginTop: 4 }}>{renderScheduleAction()}</View>
          ) : showScheduledConfirmation ? (
            <View style={{ marginTop: 4 }}>
              <SectionTitle level="subsection" style={{ lineHeight: 18 }}>
                {t("care.treatment_flows.scheduled_for_date", {
                  params: {
                    date: formatDate(procedureDate.dateIso ?? "", locale),
                  },
                })}
              </SectionTitle>
            </View>
          ) : showOutcomeRow ? (
            <View style={{ marginTop: 4 }}>{renderOutcomeAction()}</View>
          ) : showDetailsRow || showResultsRow ? (
            /* Both, when the step offers both — and presented as alternatives
               rather than as a list of two things to do.
               Attaching the lab report and typing the figures are two routes to
               the same record, not two steps. Rendered as twin rows they read as
               a checklist, so the report leads as the row (that is what
               "attach a document" looks like everywhere else in the hub), the
               figures follow as a pill — a visibly different kind of affordance,
               something she does herself rather than a file she hands over — and
               the rule between them says they are a choice.
               The report goes first because it is the first intention: most
               patients have the document before they have the patience to
               retype it. */
            <View style={{ marginTop: 4, gap: 8 }}>
              {showResultsRow ? renderResultsAction() : null}
              {showResultsRow && showDetailsRow ? (
                <View
                  style={{
                    flexDirection: "row",
                    alignItems: "center",
                    marginVertical: 2,
                  }}
                >
                  <View
                    style={{ flex: 1, height: 1, backgroundColor: c.medBorder }}
                  />
                  <Text
                    style={{
                      fontSize: 12,
                      color: c.medDate,
                      marginHorizontal: 12,
                    }}
                  >
                    {t("care.treatment_flows.or_alternatively")}
                  </Text>
                  <View
                    style={{ flex: 1, height: 1, backgroundColor: c.medBorder }}
                  />
                </View>
              ) : null}
              {showDetailsRow ? renderDetailsAction() : null}
            </View>
          ) : step.fields && step.fields.length > 0 ? (
            <View
              style={{ marginTop: step.contentType === "medications" ? 4 : 0 }}
            >
              {step.fields
                .filter(
                  (field) =>
                    (!field.showIf ||
                      stepData[field.showIf.field] === field.showIf.equals) &&
                    // While scheduling a procedure (no date / future date) only
                    // the date field is relevant — the outcome fields (counts,
                    // notes) describe what happened, which hasn't yet.
                    (!isPlanningProcedure ||
                      field.type === "datetime" ||
                      field.type === "date") &&
                    // Mode fields already render in the block above the
                    // schedule cascade, in every state — skip them here so
                    // they don't render twice.
                    field.showInPlanning !== true,
                )
                .map((field) => (
                  <View key={field.name} style={{ marginBottom: 12 }}>
                    <Text
                      className="font-dm-sans"
                      style={{
                        fontSize: 13,
                        fontWeight: "500",
                        color: c.label,
                        marginBottom: 6,
                      }}
                    >
                      {field.label}
                    </Text>
                    {field.type === "select" && field.options ? (
                      renderSelectField(field, field.options)
                    ) : field.type === "dropdown" ? (
                      (() => {
                        const options = [
                          ...(field.medicationTypeFilter
                            ? dbMedications
                                .filter(
                                  (m) =>
                                    m.name &&
                                    (m.type ?? 0) &
                                      (field.medicationTypeFilter ?? 0),
                                )
                                .map((m) => m.name!)
                            : (field.options ?? [])),
                          ...(field.medicationTypeFilter ? ["Other"] : []),
                        ];
                        const currentValue =
                          (stepData[field.name] as string) ?? "";
                        const isOpen = dropdownOpen === field.name;
                        // Display label transformer — collection-method values
                        // are stored lowercase to match the API enum but should
                        // render with clinical capitalization.
                        const formatLabel = (opt: string): string => {
                          if (field.name === "collectionMethod") {
                            return tf(
                              t,
                              `care.treatment_flows.collection_methods.${opt}`,
                              opt,
                            );
                          }
                          return tf(
                            t,
                            `care.treatment_flows.options.${opt}`,
                            opt,
                          );
                        };
                        return (
                          <View>
                            <Pressable
                              onPress={() => {
                                capturePress("step_field_dropdown");
                                setDropdownOpen(isOpen ? null : field.name);
                              }}
                              className="flex-row items-center justify-between rounded-lg border px-3 py-2.5"
                              style={{
                                backgroundColor: c.fieldBg,
                                borderColor: isOpen
                                  ? c.selectedBorder
                                  : c.fieldBorder,
                              }}
                            >
                              <Text
                                style={{
                                  fontSize: 13,
                                  color: currentValue
                                    ? c.fieldText
                                    : c.fieldPlaceholder,
                                }}
                              >
                                {currentValue === "Other"
                                  ? (stepData[
                                      `${field.name}Custom`
                                    ] as string) ||
                                    (field.name === "triggerMedication"
                                      ? (stepData.triggerMedicationCustom as string) ||
                                        t(
                                          "care.treatment_flows.options.other_enter_below",
                                        )
                                      : t(
                                          "care.treatment_flows.options.other_enter_below",
                                        ))
                                  : currentValue
                                    ? formatLabel(currentValue)
                                    : field.placeholder ||
                                      t(
                                        "care.treatment_flows.options.select_placeholder",
                                      )}
                              </Text>
                              <ChevronDown size={14} color={c.label} />
                            </Pressable>
                            {isOpen && (
                              <View
                                style={{
                                  marginTop: 4,
                                  backgroundColor: c.dropdownBg,
                                  borderWidth: 1,
                                  borderColor: c.dropdownBorder,
                                  borderRadius: 8,
                                  maxHeight: 200,
                                  overflow: "hidden",
                                }}
                              >
                                <ScrollView nestedScrollEnabled>
                                  {options.map((opt) => {
                                    const isSelected = currentValue === opt;
                                    return (
                                      <Pressable
                                        key={opt}
                                        onPress={() => {
                                          capturePress("step_field_option");
                                          handleFieldChange(field.name, opt);
                                          setDropdownOpen(null);
                                          if (
                                            field.name === "triggerMedication"
                                          ) {
                                            if (opt !== "Other") {
                                              handleFieldChange(
                                                "triggerDose",
                                                TRIGGER_DEFAULT_DOSES[opt] ??
                                                  "",
                                              );
                                            } else {
                                              handleFieldChange(
                                                "triggerDose",
                                                "",
                                              );
                                              setCustomMedConfirmed(false);
                                            }
                                          }
                                        }}
                                        className={
                                          opt === "Other"
                                            ? "m-2 flex-row items-center gap-3 rounded-xl border p-3"
                                            : "px-3 py-2.5"
                                        }
                                        style={
                                          opt === "Other"
                                            ? {
                                                borderStyle: "dashed",
                                                borderColor: c.otherBorder,
                                              }
                                            : {
                                                backgroundColor: isSelected
                                                  ? withAlpha(
                                                      c.fieldSelected,
                                                      0.09,
                                                    )
                                                  : "transparent",
                                                borderBottomWidth: 1,
                                                borderBottomColor:
                                                  c.dropdownSep,
                                              }
                                        }
                                      >
                                        {opt === "Other" ? (
                                          <>
                                            <Plus
                                              size={16}
                                              color={c.otherText}
                                            />
                                            <Text
                                              style={{
                                                fontSize: 13,
                                                fontWeight: "500",
                                                color: c.otherText,
                                              }}
                                            >
                                              {t(
                                                "care.treatment_flows.options.other",
                                              )}
                                            </Text>
                                          </>
                                        ) : (
                                          <Text
                                            style={{
                                              fontSize: 13,
                                              color: isSelected
                                                ? c.fieldSelected
                                                : c.dropdownItem,
                                              fontWeight: isSelected
                                                ? "600"
                                                : "400",
                                            }}
                                          >
                                            {formatLabel(opt)}
                                          </Text>
                                        )}
                                      </Pressable>
                                    );
                                  })}
                                  {options.length === 0 && (
                                    <Text
                                      style={{
                                        padding: 12,
                                        fontSize: 13,
                                        color: c.noMedText,
                                      }}
                                    >
                                      {t(
                                        "care.treatment_flows.medications.none_found",
                                      )}
                                    </Text>
                                  )}
                                </ScrollView>
                              </View>
                            )}
                            {currentValue === "Other" &&
                              field.name === "triggerMedication" &&
                              !customMedConfirmed && (
                                <View
                                  style={[
                                    isLight
                                      ? cardSurface({ variant: "panel" })
                                      : {
                                          borderRadius: 12,
                                          borderWidth: 1,
                                          borderColor: c.customBorder,
                                          backgroundColor: c.customBg,
                                        },
                                    { marginTop: 8, overflow: "hidden" },
                                  ]}
                                >
                                  <View
                                    style={{
                                      borderBottomWidth: 1,
                                      borderBottomColor: c.dropdownSep,
                                      paddingHorizontal: 16,
                                      paddingVertical: 10,
                                    }}
                                  >
                                    <Text
                                      style={{
                                        fontSize: 12,
                                        fontWeight: "600",
                                        color: c.customAccent,
                                      }}
                                    >
                                      Add custom medication
                                    </Text>
                                  </View>
                                  <View style={{ padding: 16 }}>
                                    <TextInput
                                      value={
                                        (stepData.triggerMedicationCustom as string) ??
                                        ""
                                      }
                                      onChangeText={(text) =>
                                        handleFieldChange(
                                          "triggerMedicationCustom",
                                          text,
                                        )
                                      }
                                      placeholder="Enter medication name..."
                                      placeholderTextColor={c.fieldPlaceholder}
                                      autoFocus
                                      style={{
                                        width: "100%",
                                        borderRadius: 12,
                                        borderWidth: 1,
                                        borderColor: c.customInputBorder,
                                        backgroundColor: c.customInputBg,
                                        paddingHorizontal: 16,
                                        paddingVertical: 12,
                                        fontSize: 13,
                                        color: c.customInputText,
                                      }}
                                    />
                                    <View className="mt-3 flex-row gap-2">
                                      <CompactActionButton
                                        label="Cancel"
                                        action="custom_medication_cancel"
                                        onPress={() => {
                                          handleFieldChange(
                                            "triggerMedication",
                                            "",
                                          );
                                          handleFieldChange(
                                            "triggerMedicationCustom",
                                            "",
                                          );
                                          setCustomMedConfirmed(false);
                                        }}
                                        variant="secondary"
                                        flexGrow={1}
                                        flexBasis={0}
                                      />
                                      <CompactActionButton
                                        label="Add"
                                        action="custom_medication_add"
                                        variant="primary"
                                        onPress={() =>
                                          setCustomMedConfirmed(true)
                                        }
                                        flexGrow={1}
                                        flexBasis={0}
                                      />
                                    </View>
                                  </View>
                                </View>
                              )}
                            {currentValue === "Other" &&
                              field.name !== "triggerMedication" && (
                                <TextInput
                                  value={
                                    (stepData[
                                      `${field.name}Custom`
                                    ] as string) ?? ""
                                  }
                                  onChangeText={(text) =>
                                    handleFieldChange(
                                      `${field.name}Custom`,
                                      text,
                                    )
                                  }
                                  placeholder={`Enter ${field.label.toLowerCase()}...`}
                                  placeholderTextColor={c.fieldPlaceholder}
                                  autoFocus
                                  style={{
                                    marginTop: 8,
                                    width: "100%",
                                    borderRadius: 8,
                                    borderWidth: 1,
                                    borderColor: c.fieldBorder,
                                    backgroundColor: c.fieldBg,
                                    paddingHorizontal: 12,
                                    paddingVertical: 10,
                                    fontSize: 13,
                                    color: c.fieldText,
                                  }}
                                />
                              )}
                          </View>
                        );
                      })()
                    ) : field.type === "textarea" ? (
                      <TextField
                        value={(stepData[field.name] as string) ?? ""}
                        onChangeText={(text) =>
                          handleFieldChange(field.name, text)
                        }
                        placeholder={field.placeholder}
                        notes
                      />
                    ) : field.type === "datetime" ? (
                      renderDateTimePicker ? (
                        renderDateTimePicker({
                          value: (stepData[field.name] as string) ?? null,
                          onChange: (isoString) =>
                            handleFieldChange(field.name, isoString),
                          placeholder: field.placeholder,
                        })
                      ) : (
                        <DateTimeFieldPicker
                          value={(stepData[field.name] as string) ?? null}
                          onChange={(isoString) =>
                            handleFieldChange(field.name, isoString)
                          }
                        />
                      )
                    ) : field.type === "upload" ? (
                      <TreatmentUploadField
                        value={(stepData[field.name] as string) ?? null}
                        onChange={(docId, uploadId) => {
                          handleFieldChange(field.name, docId);
                          if (step.id === "pgt_testing") {
                            handleFieldChange("pgtResultUploadId", uploadId);
                          }
                        }}
                        label={`Upload ${field.label}`}
                        tags={["medical_records", `step:${step.id}`]}
                        bg={c.fieldBg}
                        border={c.fieldBorder}
                        text={c.fieldText}
                      />
                    ) : field.type === "specimen" ? (
                      renderSpecimenField ? (
                        renderSpecimenField({
                          fieldName: field.name,
                          value: (stepData[field.name] as string) ?? null,
                          onChange: (id) => handleFieldChange(field.name, id),
                        })
                      ) : null
                    ) : field.type === "dose" ? (
                      <DoseField
                        value={String(stepData[field.name] ?? "")}
                        onChange={(next) => handleFieldChange(field.name, next)}
                        placeholder={field.placeholder}
                        dropdownKey={field.name}
                        dropdownOpen={dropdownOpen}
                        setDropdownOpen={setDropdownOpen}
                        colors={{
                          fieldBg: c.fieldBg,
                          fieldBorder: c.fieldBorder,
                          fieldText: c.fieldText,
                          fieldPlaceholder: c.fieldPlaceholder,
                          selectedBorder: c.selectedBorder,
                          label: c.label,
                          dropdownBg: c.dropdownBg,
                          dropdownBorder: c.dropdownBorder,
                          dropdownItem: c.dropdownItem,
                          dropdownSep: c.dropdownSep,
                          fieldSelected: c.fieldSelected,
                        }}
                      />
                    ) : (
                      // A value with its unit inside the field: a fixed unit
                      // after the value, or the units it can be in.
                      (() => {
                        const unitChoice = field.unitChoice;
                        const { unit: unitValue, choices: units } =
                          resolveStepFieldUnits(field, stepData);
                        return (
                          <MoonValueInput
                            value={String(stepData[field.name] ?? "")}
                            onChangeText={(text) =>
                              handleFieldChange(
                                field.name,
                                field.type === "number"
                                  ? text
                                    ? Number(text)
                                    : null
                                  : text,
                              )
                            }
                            placeholder={field.placeholder}
                            accessibilityLabel={field.label}
                            keyboardType={
                              field.type === "number" ? "numeric" : "default"
                            }
                            suffix={units ? undefined : unitValue}
                            trailing={
                              unitChoice && units ? (
                                <MoonUnitToggle
                                  units={units}
                                  value={unitValue ?? null}
                                  onChange={(unit) =>
                                    handleFieldChange(unitChoice.field, unit)
                                  }
                                  accessibilityLabel={t("test_result.unit")}
                                  action={`${field.name}_unit`}
                                />
                              ) : undefined
                            }
                          />
                        );
                      })()
                    )}
                  </View>
                ))}
            </View>
          ) : null}

          {/* A record attached to this procedure, not an answer about it.
              It used to render above the fields, which put a navigation row
              between "How did your IUI go?" and the first thing being asked —
              cutting the form in two. After the fields it reads as what it is:
              one more thing you can attach, once you have said what happened. */}
          {renderLinkageAction ? (
            <View style={{ marginTop: 4 }}>{renderLinkageAction()}</View>
          ) : null}

          {/* Action Buttons */}
          <View style={{ gap: 12 }}>
            {/* Primary CTA: the warm pill, so the action matches the rest of
                the Treatment Hub design. When the canonical schedule row is
                shown, the row is the sole action. */}
            {!hidePendingCompleteAction &&
              !showScheduleRow &&
              !showOutcomeRow &&
              !showResultsRow &&
              !showDetailsRow && (
                <>
                  <CompactActionButton
                    label={effectivePendingLabel}
                    onPress={() => onComplete(localData)}
                    variant="primary"
                    minWidth={168}
                    minHeight={54}
                    disabled={!canComplete}
                  />
                  {missingRequiredField ? (
                    <Text
                      style={{
                        fontSize: 12,
                        color: c.label,
                        textAlign: "center",
                        marginTop: 4,
                      }}
                    >
                      {t("care.treatment_flows.state.required_field", {
                        params: { field: missingRequiredField.label },
                      })}
                    </Text>
                  ) : null}
                </>
              )}

            {/* Skip link.
                Not suppressed by the scheduling row. A step that declares itself
                skippable is skippable precisely when there is nothing to
                schedule: `sperm_collection` on frozen sperm is the case that
                found this — no collection to date, and the row offering to
                schedule one was the only thing on the card, so there was no way
                to say it will not happen. The same holds for a `transfer` in the
                two flows that mark it skippable: a cycle freezing everything has
                no transfer to book either.
                `showOutcomeRow` still suppresses it — that row appears once the
                procedure has happened, and skipping something already recorded
                is not a thing to offer. */}
            {step.skippable && !hideSkip && !showOutcomeRow && (
              <CompactActionButton
                label={
                  step.skipText ??
                  t("care.treatment_flows.actions.default_skip")
                }
                onPress={onSkip}
                variant="secondary"
                minWidth={118}
                minHeight={54}
              />
            )}
          </View>
        </>
      )}
    </View>
  );
}

// =============================================================================
// Egg Freezing Migration: 3-step → 5-step
// =============================================================================

/**
 * Migrates old 3-step egg_freezing stepProgress to the new 5-step format.
 * Idempotent — if new-format IDs already exist, returns the array as-is.
 *
 * Mapping:
 *   old `protocol`       → `stimulation` (medications carry over)
 *   old `protocol` data  → `trigger_shot` entry if trigger fields present
 *   old `egg_retrieval`  → narrowed `egg_retrieval` + new `freezing`
 */
export function migrateEggFreezingStepProgress(
  stepProgress: TreatmentStepProgress[] | null,
): TreatmentStepProgress[] {
  if (!stepProgress || stepProgress.length === 0) return stepProgress ?? [];

  // Already migrated — new-format IDs present
  if (
    stepProgress.some(
      (s) =>
        s.stepId === "stimulation" ||
        s.stepId === "trigger_shot" ||
        s.stepId === "freezing",
    )
  ) {
    return stepProgress;
  }

  const result: TreatmentStepProgress[] = [];

  for (const sp of stepProgress) {
    if (sp.stepId === "protocol") {
      // protocol → stimulation (keep everything except trigger fields)
      const stimData = { ...(sp.data ?? {}) };
      delete stimData.triggerTime;
      delete stimData.triggerDate;
      delete stimData.triggerMedication;
      result.push({ ...sp, stepId: "stimulation", data: stimData });

      // Extract trigger data into a trigger_shot entry if present
      const oldData = sp.data ?? {};
      if (oldData.triggerTime || oldData.triggerDate) {
        result.push({
          stepId: "trigger_shot",
          status: "completed" as const,
          completedAt: sp.completedAt,
          medications: [],
          notes: null,
          data: {
            triggerDate: (oldData.triggerDate as string) ?? null,
            triggerTime: (oldData.triggerTime as string) ?? null,
            triggerMedication: (oldData.triggerMedication as string) ?? null,
          },
        });
      }
    } else if (sp.stepId === "egg_retrieval") {
      const oldData = sp.data ?? {};

      // Narrowed egg_retrieval — only retrieval-specific fields
      result.push({
        ...sp,
        stepId: "egg_retrieval",
        data: {
          eggsCollected: oldData.eggsCollected ?? null,
          procedureDate: oldData.procedureDate ?? null,
          notes: oldData.notes ?? null,
        },
      });

      // New freezing step — maturity/cryo fields
      const hasFreezeData =
        oldData.matureEggs != null ||
        oldData.eggsFrozen != null ||
        oldData.storageLocation != null ||
        oldData.batchId != null;
      if (hasFreezeData) {
        result.push({
          stepId: "freezing",
          status: sp.status,
          completedAt: sp.completedAt,
          medications: [],
          notes: null,
          data: {
            matureEggs: oldData.matureEggs ?? null,
            eggsFrozen: oldData.eggsFrozen ?? null,
            storageLocation: oldData.storageLocation ?? null,
            batchId: oldData.batchId ?? null,
          },
        });
      }
    } else {
      result.push(sp);
    }
  }

  return result;
}

export { TREATMENT_FLOWS } from "./treatmentFlows";

// =============================================================================
// Translation layer for TREATMENT_FLOWS
// =============================================================================

/**
 * Returns t(key) if a translation exists, otherwise returns the English
 * fallback. This lets us add translation keys incrementally without breaking
 * screens when a key hasn't been added yet.
 */
